/**
 * Pure H OS · Sprint 12 · Marketing mocks — types + live views.
 * Payload now lives in MongoDB `pureh_*` collections. Note the two
 * name-collisions with growth and workspace mocks:
 *   • `CHANNELS`  → fixtures key `marketing_channels`
 *   • `TASKS`     → fixtures key `tasks` (marketing tasks won the collision;
 *                    workspace tasks are stored as `workspace_tasks`).
 */
import { liveArray, liveSingleton } from "../services/store";

export type CampaignStatus = "planned" | "in-flight" | "live" | "wrapping" | "done";
export type Campaign = {
  id: string;
  name: string;
  sku: string;
  channels: string[];
  status: CampaignStatus;
  startDate: string;
  endDate: string;
  owner: string;
  budgetEGP: number;
  spendEGP: number;
  reach: number;
  ctr: number;
  roas: number;
  color: string;
};
export const CAMPAIGNS: Campaign[] = liveArray<Campaign>("campaigns");

export type ContentStage = "idea" | "draft" | "review" | "scheduled" | "published";
export type ContentType = "post" | "reel" | "video" | "story" | "email" | "article";
export type ContentItem = {
  id: string;
  title: string;
  type: ContentType;
  channel: string;
  campaignId?: string;
  stage: ContentStage;
  owner: string;
  scheduledFor?: string;
  aiAssisted?: boolean;
  thumb: string;
};
export const CONTENT: ContentItem[] = liveArray<ContentItem>("content");

export type CreativeStatus = "pending" | "approved" | "changes-requested";
export type CreativeAsset = {
  id: string;
  title: string;
  campaignId: string;
  format: "1:1" | "4:5" | "9:16" | "16:9";
  version: number;
  status: CreativeStatus;
  reviewer: string;
  reviewedRelative: string;
  notes: string;
  aiScore?: number;
  thumb: string;
};
export const CREATIVES: CreativeAsset[] = liveArray<CreativeAsset>("creatives");

export type CopyTone = "editorial" | "poetic" | "confident" | "warm" | "playful";
export type CopyFormat = "headline" | "caption" | "email-subject" | "product-desc" | "ad";
export const COPY_TONES: { key: CopyTone; label: string; hint: string }[] = liveArray<{ key: CopyTone; label: string; hint: string }>("copy_tones");
export const COPY_FORMATS: { key: CopyFormat; label: string }[] = liveArray<{ key: CopyFormat; label: string }>("copy_formats");

export type CopyDraft = {
  id: string;
  title: string;
  format: CopyFormat;
  tone: CopyTone;
  body: string;
  createdRelative: string;
  favourite: boolean;
};
export const COPY_HISTORY: CopyDraft[] = liveArray<CopyDraft>("copy_history");

export type CreatorTier = "nano" | "micro" | "macro" | "vip";
export type CreatorStatus = "prospect" | "pitching" | "contracted" | "live" | "wrap";
export type Creator = {
  id: string;
  handle: string;
  name: string;
  tier: CreatorTier;
  audience: number;
  engagement: number;
  status: CreatorStatus;
  campaigns: string[];
  city: string;
  costEGP: number;
  aiFit: number;
};
export const CREATORS: Creator[] = liveArray<Creator>("creators");

export type ChannelStat = {
  key: string;
  name: string;
  reach: number;
  engagement: number;
  ctr: number;
  spendEGP: number;
  revenueEGP: number;
  delta: number;
  trend: "up" | "down" | "flat";
};
export const CHANNELS: ChannelStat[] = liveArray<ChannelStat>("marketing_channels");

export type TaskStatus = "todo" | "in-progress" | "blocked" | "done";
export type MktTask = {
  id: string;
  title: string;
  campaignId?: string;
  owner: string;
  dueRelative: string;
  status: TaskStatus;
  severity: "high" | "medium" | "low";
};
export const TASKS: MktTask[] = liveArray<MktTask>("tasks");

// ── Overview KPIs & AI signals (singleton) ──────────────────────
type Kpi = { key: string; label: string; value: string; delta: number; trend: string; hint?: string };
type MktSignal = { id: string; tag: "risk" | "opportunity" | "insight"; title: string; body: string };
export type MarketingOverview = { headline: string; subline: string; kpis: Kpi[]; signals: MktSignal[] };
export const MARKETING_OVERVIEW: MarketingOverview = liveSingleton<MarketingOverview>(
  "marketing_overview",
  { headline: "", subline: "", kpis: [], signals: [] },
);
